import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, ChartBar, Warning} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, fmtPct, PERIOD_OPTIONS} from './_lib';

const STEP_KEYS = ['send','delivery','open','click','replied','bounce'];
const STEP_LABEL = {send:'Sent', delivery:'Delivered', open:'Opens', click:'Clicks', replied:'Replied', bounce:'Bounces'};

export default function PersonaFunnel() {
	const navigate = useNavigate();
	const [period, setPeriod] = useState('7d');
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true); setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(`${NEUGENCE_API_BASE}/api/v1/personas/funnel?period=${period}`, {headers});
			setData(res.data || {});
		} catch (e) { setError(e.message); }
		finally { setLoading(false); }
	}, [period]);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);
	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	const rows = (data && data.rows) || [];
	// group by persona, side-by-side variant
	const byPersona = {};
	rows.forEach(r => { (byPersona[r.persona] = byPersona[r.persona] || []).push(r); });
	const personas = Object.keys(byPersona).sort();

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-6xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
							<ChartBar size={28} weight="duotone" color="#10b981"/> Persona funnels
						</h1>
						<p className="text-sm text-slate-500 mt-1">Sent → Delivered → Opens → Clicks → Replied per persona × variant. A/B winner indicator.</p>
					</div>
					<div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1">
						{PERIOD_OPTIONS.map(p => (
							<button key={p.key} onClick={() => setPeriod(p.key)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period===p.key?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>{p.label}</button>
						))}
					</div>
				</div>

				{error && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6"><Warning size={18} className="text-amber-600 inline mr-2"/>{String(error)}</div>}
				{loading && <div className="text-center text-sm text-slate-500 py-20">Loading…</div>}

				{!loading && personas.length === 0 && (
					<div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">No persona data in this window yet.</div>
				)}

				{!loading && personas.map(persona => {
					const variants = byPersona[persona].sort((a,b) => (a.variant||'').localeCompare(b.variant||''));
					// pick winner by click rate
					let winner = null;
					if (variants.length === 2) {
						const score = v => (v.click||0) / Math.max(v.delivery||1, 1);
						winner = score(variants[0]) > score(variants[1]) ? variants[0].variant : variants[1].variant;
					}
					return (
						<div key={persona} className="rounded-2xl border border-slate-200 bg-white p-5 mb-4">
							<div className="font-semibold text-slate-900 mb-3 text-lg">{persona}</div>
							<table className="w-full text-sm">
								<thead className="text-xs uppercase tracking-wide text-slate-500">
									<tr>
										<th className="text-left py-2 w-16">Variant</th>
										{STEP_KEYS.map(k => <th key={k} className="text-right px-3 py-2">{STEP_LABEL[k]}</th>)}
										<th className="text-right px-3 py-2">Open %</th>
										<th className="text-right px-3 py-2">Click %</th>
										<th className="text-right px-3 py-2">Winner</th>
									</tr>
								</thead>
								<tbody>
									{variants.map(v => (
										<tr key={v.variant} className="border-t border-slate-100">
											<td className="py-2 font-medium text-slate-900">{v.variant}</td>
											{STEP_KEYS.map(k => <td key={k} className="text-right px-3 py-2 text-slate-700">{v[k]||0}</td>)}
											<td className="text-right px-3 py-2 text-slate-700">{fmtPct(v.open, v.delivery)}</td>
											<td className="text-right px-3 py-2 text-slate-700">{fmtPct(v.click, v.delivery)}</td>
											<td className="text-right px-3 py-2">
												{winner && winner === v.variant && <span className="inline-flex rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-medium">🏆 wins</span>}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					);
				})}
			</div>
		</div>
	);
}
